import React, { useState } from "react";
import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import Popover, { PopoverAlign } from "components/ui/popover/Popover";
import RelevanceIcon from "lib/icons/Relevance";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { SignalEntityType } from "lib/utils/signalUtils";
import { NewRelevanceScoreContent } from "./NewRelevanceScoreContent";

import css from "./RelevanceScore.module.scss";

type RelevanceScoreProps = {
  id: string;
  relevanceScore?: number | null;
  eventAttributes?: EventData;
  entityType: SignalEntityType;
  popoverAlignment?: PopoverAlign;
};

function convertScoreToText(relevanceScore?: number | null) {
  switch (relevanceScore) {
    case 3:
      return "High Relevance";
    case 2:
      return "Medium Relevance";
    case 1:
      return "Low Relevance";
    case 0:
    default:
      return "No Score";
  }
}

export function NewRelevanceScore({
  id,
  relevanceScore,
  eventAttributes,
  entityType,
  popoverAlignment = "start",
}: RelevanceScoreProps) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const { logEvent } = useTracking();

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <Popover
        hideArrow
        open={open}
        side="bottom"
        align={popoverAlignment}
        onOpenChange={(open) => {
          setOpen(open);
          if (open) {
            logEvent(EventNames.relevanceScoreClicked, eventAttributes);
          }
        }}
        trigger={
          <Button
            buttonStyle="inverse"
            aria-label={"Relevance score"}
            className={css.relevanceScoreBtn}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <RelevanceIcon relevance={relevanceScore} darkMode={hover} />
            <span className={css.relevanceNumber}>{relevanceScore ?? 0}</span>
          </Button>
        }
      >
        <div className={css.popoverHeader}>
          <div className={css.popoverTitle}>
            <RelevanceIcon relevance={relevanceScore} />
            <span className={css.relevanceTitle}>{convertScoreToText(relevanceScore)}</span>
          </div>
          <Button buttonType="tertiary" blackText onClick={() => setOpen(false)} icon="xClose" />
        </div>
        <NewRelevanceScoreContent
          entityId={id}
          entityType={entityType}
          eventAttributes={eventAttributes}
        />
      </Popover>
    </Container>
  );
}

const Container = styled.div({
  cursor: "auto",
});
